import {useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode} from 'react';

export interface CallMenuItem {
    readonly id: string;
    readonly label: string;
    readonly icon?: ReactNode;
    readonly testId?: string;
    readonly onSelect: () => void;
}

interface CallMenuProps {
    readonly items: readonly CallMenuItem[];
    readonly placement: 'above' | 'above-end';
    readonly trigger: (props: {open: boolean; toggle: () => void; menuId: string}) => ReactNode;
    readonly extra?: ReactNode;
}

export const CallMenu = ({items, placement, trigger, extra}: CallMenuProps) => {
    const [open, setOpen] = useState(false);
    const menuId = useId();
    const container = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const close = useCallback(() => setOpen(false), []);
    const toggle = useCallback(() => setOpen(current => !current), []);

    useEffect(() => {
        if (!open) {
            return;
        }
        itemRefs.current[0]?.focus();
        const onPointerDown = (event: PointerEvent) => {
            if (!container.current?.contains(event.target as Node)) {
                close();
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [close, open]);

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            close();
            return;
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
            return;
        }
        event.preventDefault();
        const focusable = itemRefs.current.filter((item): item is HTMLButtonElement => !!item);
        const index = focusable.indexOf(document.activeElement as HTMLButtonElement);
        const step = event.key === 'ArrowDown' ? 1 : -1;
        focusable[(index + step + focusable.length) % focusable.length]?.focus();
    };

    return (
        <div ref={container} className="relative" onKeyDown={onKeyDown}>
            {trigger({open, toggle, menuId})}
            {open && (
                <div
                    id={menuId}
                    role="menu"
                    className={`phq-call-swap absolute bottom-[calc(100%+8px)] z-10 flex min-w-[220px] flex-col gap-0.5 rounded-2xl border border-[var(--phq-call-line)] bg-[var(--phq-call-canvas)] p-2 phq-call-shadow-menu ${placement === 'above-end' ? 'right-0' : 'left-1/2 -translate-x-1/2'}`}
                >
                    {extra}
                    {items.map((item, index) => (
                        <button
                            key={item.id}
                            ref={element => {
                                itemRefs.current[index] = element;
                            }}
                            type="button"
                            role="menuitem"
                            data-testid={item.testId}
                            onClick={() => {
                                close();
                                item.onSelect();
                            }}
                            className="flex h-11 items-center gap-3 rounded-lg px-3 text-left text-sm font-medium text-[var(--phq-call-ink)] hover:bg-[var(--phq-call-surface-muted)] focus-visible:bg-[var(--phq-call-surface-muted)] focus-visible:outline-none"
                        >
                            {item.icon && <span className="shrink-0 text-[var(--phq-call-ink-muted)]">{item.icon}</span>}
                            <span className="truncate">{item.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
